import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Divider, Skeleton } from '@mui/material';
import DonutChart from './DonutChart';
import HeaderSection from './HeaderSection';
import RecommendationsSection from './Recommendation';
import { useLazyGetCandidateCountQuery } from '@/redux/apis/manage-folder';
import { getFolderIdFromURL } from '@/utils/getFolderId';
import '@foundit/barchart/dist/bargraphs.css';
import { Bargraphs } from '@foundit/barchart/dist/bargraphs';

type Props = {
  assessmentCampaignId?: string | null;
  setAssessmentId?: any;
  setAssessmentList: Function;
  assessmentList: any[];
}

const AssessmentLayout : React.FC<Props> = ({ assessmentCampaignId ,setAssessmentId=()=>{},
setAssessmentList=()=>{},assessmentList=[],
}) => {
  const [donutChartData, setDonutChartData] = useState<any>([]);
  const [barGraphData, setBarGraphData] = useState<any>([]);
  const [bottomStats, setBottomStats] = useState<any>([]);
  const [recommendationData, setRecommendationData] = useState<any>([]);
  const [followUpCount, setFollowUpCount] = useState<number>(0);
  const [triggerGetCandidateCount, { data, isLoading }] = useLazyGetCandidateCountQuery();
  const folderId = getFolderIdFromURL();

  useEffect(() => {
   
    const campaignId = assessmentCampaignId;

    // Trigger the API call
    triggerGetCandidateCount({ folderId, campaignId : campaignId });
  
  }, [triggerGetCandidateCount,assessmentCampaignId]);

  useEffect(() => {
    if (data) {
      // Populate Donut Chart Data
      const donutData = [
        data.notRecommended,
        data.cautiouslyRecommended,
        data.recommended,
        data.highlyRecommended,
        data?.disqualified || 0,
      ];
      setDonutChartData(donutData);
      setFollowUpCount(data?.followUp);

      // Safely calculate percentages for Bar Graph and Bottom Stats
      const allInvitedCandidates = data.allInvitedCandidates || 1; // Avoid division by 0 by using a fallback of 1

      // Populate Bar Graph Data
      const recommendedCount = Number(data?.highlyRecommended) + Number(data?.recommended) + Number(data?.cautiouslyRecommended);
      const barData = [
        { label: 'Total Invited', value: allInvitedCandidates, percentage: '100' },
        {
          label: 'Total Attempted',
          value: data.attempted,
          percentage: ((data.attempted / allInvitedCandidates) * 100).toFixed(0),
        },
        {
          label: 'Total Recommended',
          value: recommendedCount,
          percentage: ((recommendedCount / allInvitedCandidates) * 100).toFixed(0),
        },
      ];
      setBarGraphData(barData);

      // Populate Bottom Stats
      const stats = [
        {
          label: 'Invites Cancelled',
          value: data.cancelledInvite,
          // percentage: ((data.cancelledInvite / allInvitedCandidates) * 100).toFixed(2),
          percentage: null,
        },
        {
          label: 'Non-Contactable',
          value: data.nonContactable,
          // percentage: ((data.nonContactable / allInvitedCandidates) * 100).toFixed(2),
          percentage: null,
        },
        // {
        //   label: 'Already Attempted',
        //   value: data.attemptedInPast,
        //   percentage: ((data.attemptedInPast / invited) * 100).toFixed(2),
        // },
      ];
      setBottomStats(stats);

      // Populate Recommendations Data
      const recommendations = [
        { count: data.highlyRecommended, label: 'Highly Recommended', color: '#1b5e20' },
        { count: data.recommended, label: 'Recommended', color: '#4caf50' },
        { count: data.cautiouslyRecommended, label: 'Cautiously Recommended', color: '#c8e6c9' },
        { count: data.notRecommended, label: 'Not Recommended', color: '#d32f2f' },
        { count: data?.disqualified || 0, label: 'Disqualified', color: '#e8e8eb' },
      ];
      setRecommendationData(recommendations);
    }
  }, [data]);

  return (
    <Box
      sx={{
        backgroundColor: '#f7f9fc',
        borderRadius: '8px',
        // boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        // overflow: 'hidden',
        boxSizing: 'border-box',
        width: '40%',
      }}
    >
      {/* Header Section */}
      <HeaderSection setAssessmentId={setAssessmentId} assessmentCampaignId={assessmentCampaignId}
      assessmentList={assessmentList}
      setAssessmentList={setAssessmentList}
      followUpCount={followUpCount}
      />

      {/* Main Content with 20px margin from the header */}
     {isLoading ?
      <Box sx={{padding: '20px'}}><Skeleton variant="rounded" width='100%' height={388} /></Box>
     : <Box sx={{ padding: '20px', background: '#FFFFFF', borderRadius: '0 0 8px 8px' }}>
       <Box sx={{fontWeight: 600, fontSize: '14px', marginBottom: '20px'}}>Candidate Recommendations</Box>
        <Grid container spacing={2} sx={{justifyContent: 'space-between'}}>
          {/* Left Circular Chart Section */}
          <Grid item xs={5}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                textAlign: 'center',
                marginLeft: '36px',
              }}
            >
              <DonutChart data={donutChartData} />
            </Box>
          </Grid>

          {/* Right Recommendations Section */}
          <Grid item xs={7} sx={{maxWidth: '50%', padding: '0'}}>
            <RecommendationsSection data={recommendationData} />
          </Grid>
        </Grid>


        {/* Bar Charts Section */}
        <Grid container spacing={2} marginTop='20px'>
          <Grid item xs={12}>
            <Bargraphs
             height={150}
             width="100%"
             data={barGraphData}
             loading={isLoading}
             barWidth='114px'
            />
          </Grid>
        </Grid>

        {/* Bottom Statistics Section */}
        <Box
          sx={{ display: 'flex', justifyContent: 'start', gap: '40px', flexWrap: 'wrap', marginTop: '16px' }}
        >
          {bottomStats.map((stat:any, index:any) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#6b7280',
              }}
            >
              <Box
                sx={{
                  width: '10px',
                  height: '8px',
                  backgroundColor: '#A5A3AE',
                  borderRadius: '2px',
                  marginRight: '8px',
                }}
              />
              <Typography sx={{fontSize: '12px'}}>
                {stat.label} - {stat.value}
                {' '}
                {stat.percentage && `(${stat.percentage}%)`}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>}
    </Box>
  );
};

export default AssessmentLayout;
